import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useFormatting } from '@ayeldo/frontend-core/hooks/useFormatting';

import { useIdpSession } from '../../hooks/useIdpSession';
import { adminApi, type GroupSummary } from '../../services/admin-api';
import { APIError } from '../../services/idp-api';

export function GroupsPage() {
  const { t } = useTranslation('idp');
  const navigate = useNavigate();
  const { formatDate } = useFormatting();
  const { isAuthenticated, isLoading: sessionLoading } = useIdpSession();
  const [groups, setGroups] = useState<GroupSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionLoading && !isAuthenticated) {
      navigate('/auth/signin', { replace: true });
    }
  }, [isAuthenticated, sessionLoading, navigate]);

  useEffect(() => {
    let isMounted = true;

    async function load() {
      setIsLoading(true);
      setErrorMessage(null);
      try {
        const result = await adminApi.listGroups();
        if (!isMounted) return;
        setGroups(result.data);
      } catch (error) {
        if (!isMounted) return;
        if (error instanceof APIError) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage(t('groups.errors.default'));
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    void load();

    return () => {
      isMounted = false;
    };
  }, [t]);

  if (sessionLoading || isLoading) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h5" component="h1" fontWeight={700}>
        {t('groups.title')}
      </Typography>

      {errorMessage !== null ? <Alert severity="error">{errorMessage}</Alert> : null}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('groups.tableHeaders.name')}</TableCell>
              <TableCell>{t('groups.tableHeaders.members')}</TableCell>
              <TableCell>{t('groups.tableHeaders.type')}</TableCell>
              <TableCell>{t('groups.tableHeaders.created')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group) => (
              <TableRow key={group.groupId}>
                <TableCell>
                  <Link
                    component={RouterLink}
                    to={`/admin/groups/${group.groupId}`}
                    underline="hover"
                  >
                    {group.name}
                  </Link>
                </TableCell>
                <TableCell>{group.memberCount}</TableCell>
                <TableCell>
                  {group.isSystem ? (
                    <Chip label={t('groups.typeSystem')} color="primary" size="small" />
                  ) : (
                    <Chip label={t('groups.typeCustom')} size="small" />
                  )}
                </TableCell>
                <TableCell>{formatDate(group.createdAt)}</TableCell>
              </TableRow>
            ))}
            {groups.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  {t('groups.emptyMessage')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
